import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Tab, Tabs, Badge, Table } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import AdminNavbar from '../components/AdminNavbar';
import GoogleFormsIntegration from '../components/GoogleFormsIntegration';

const AdminSettings = () => {
  const { currentUser, userProfile } = useAuth();
  const [systemSettings, setSystemSettings] = useState({
    general: {
      systemName: 'Access70 LMS',
      systemDescription: 'Learning Management System',
      maintenanceMode: false,
      registrationEnabled: true,
      maxFileUploadSize: 10, // MB
      sessionTimeout: 30, // minutes
      timezone: 'Africa/Johannesburg'
    },
    notifications: {
      emailEnabled: true,
      pushEnabled: true,
      smsEnabled: false,
      weeklyReportsEnabled: true,
      alertThresholds: {
        lowAttendance: 75,
        documentOverdue: 7, // days
        systemErrors: 10
      }
    },
    security: {
      passwordMinLength: 8,
      passwordRequireSpecial: true,
      sessionTimeout: 30,
      maxLoginAttempts: 5,
      twoFactorRequired: false
    },
    compliance: {
      documentRetentionDays: 2555, // 7 years
      auditLogEnabled: true,
      encryptionEnabled: true,
      backupFrequency: 'daily'
    },
    documents: {
      initialUploadDeadline: 14, // days from registration
      maxExtensionDays: 30,
      reminderDays: {
        first: 7,
        second: 3,
        final: 1
      },
      escalationDays: {
        first: 1,
        second: 7,
        final: 14
      },
      autoRemindersEnabled: true,
      facilitatorCanExtend: true
    }
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [systemLogs, setSystemLogs] = useState([]);

  useEffect(() => {
    if (currentUser && userProfile?.role === 'admin') {
      loadSystemSettings();
      loadSystemLogs();
    }
  }, [currentUser, userProfile]);

  const loadSystemSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'system'));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setSystemSettings(prevSettings => ({
          ...prevSettings,
          ...data.settings
        }));
      }
    } catch (error) {
      devError('Error loading system settings:', error);
      // Show a warning but don't break the page
      if (error.code === 'permission-denied') {
        showAlert('Settings collection not accessible. Using default settings.', 'warning');
      }
    }
  };

  const loadSystemLogs = () => {
    // Mock system logs - in real implementation, these would come from a logging service
    const mockLogs = [
      {
        id: '1',
        timestamp: new Date(Date.now() - 1000 * 60 * 30),
        level: 'INFO',
        message: 'System backup completed successfully',
        user: 'System'
      },
      {
        id: '2',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
        level: 'WARNING',
        message: 'High memory usage detected',
        user: 'System'
      },
      {
        id: '3',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4),
        level: 'INFO',
        message: 'User settings updated',
        user: currentUser?.email
      },
      {
        id: '4',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 8),
        level: 'ERROR',
        message: 'Failed to send notification email',
        user: 'System'
      },
      {
        id: '5',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 12),
        level: 'INFO',
        message: 'Database cleanup completed',
        user: 'System'
      }
    ];
    setSystemLogs(mockLogs);
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleSettingChange = (category, setting, value) => {
    setSystemSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: value
      }
    }));
  };

  const handleNestedSettingChange = (category, parentSetting, setting, value) => {
    setSystemSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [parentSetting]: {
          ...prev[category][parentSetting],
          [setting]: value
        }
      }
    }));
  };

  const handleSaveSettings = async (category) => {
    setLoading(true);
    
    try {
      await setDoc(doc(db, 'settings', 'system'), {
        settings: systemSettings,
        lastUpdated: serverTimestamp(),
        lastUpdatedBy: currentUser.uid
      }, { merge: true });
      
      showAlert(`${category} settings saved successfully!`, 'success');
    } catch (error) {
      devError('Error saving settings:', error);
      showAlert('Error saving settings', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const handleSystemMaintenance = async () => {
    setLoading(true);
    
    try {
      // Toggle maintenance mode
      const newMaintenanceMode = !systemSettings.general.maintenanceMode;
      
      await handleSettingChange('general', 'maintenanceMode', newMaintenanceMode);
      await handleSaveSettings('general');
      
      showAlert(
        `Maintenance mode ${newMaintenanceMode ? 'enabled' : 'disabled'}`, 
        newMaintenanceMode ? 'warning' : 'success'
      );
    } catch (error) {
      devError('Error toggling maintenance mode:', error);
      showAlert('Error updating maintenance mode', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const getLevelBadge = (level) => {
    const variants = {
      'INFO': 'info',
      'WARNING': 'warning',
      'ERROR': 'danger',
      'SUCCESS': 'success'
    };
    return <Badge bg={variants[level] || 'secondary'}>{level}</Badge>;
  };

  if (userProfile?.role !== 'admin') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access system settings.</p>
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <AdminNavbar currentPage="settings" />
      
      <Container className="mt-4">
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1>⚙️ System Settings</h1>
                <p className="text-muted">Configure system-wide settings and preferences</p>
              </div>
              <div>
                <Button 
                  variant={systemSettings.general.maintenanceMode ? "warning" : "outline-warning"}
                  onClick={handleSystemMaintenance}
                  disabled={loading}
                  className="me-2"
                >
                  {systemSettings.general.maintenanceMode ? '🔧 Exit Maintenance' : '🛠️ Maintenance Mode'}
                </Button>
                {systemSettings.general.maintenanceMode && (
                  <Badge bg="warning" className="fs-6">Maintenance Active</Badge>
                )}
              </div>
            </div>
          </Col>
        </Row>

        {alert.show && (
          <Alert variant={alert.type} className="mb-4">
            {alert.message}
          </Alert>
        )}

        <Tabs defaultActiveKey="general" className="mb-4">
          <Tab eventKey="general" title="General">
            <Card>
              <Card.Header>
                <h5>General System Settings</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>System Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={systemSettings.general.systemName}
                        onChange={(e) => handleSettingChange('general', 'systemName', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Timezone</Form.Label>
                      <Form.Select
                        value={systemSettings.general.timezone}
                        onChange={(e) => handleSettingChange('general', 'timezone', e.target.value)}
                      >
                        <option value="Africa/Johannesburg">Africa/Johannesburg (SAST)</option>
                        <option value="UTC">UTC</option>
                        <option value="Africa/Cape_Town">Africa/Cape_Town</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>System Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={systemSettings.general.systemDescription}
                    onChange={(e) => handleSettingChange('general', 'systemDescription', e.target.value)}
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Max File Upload Size (MB)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="100"
                        value={systemSettings.general.maxFileUploadSize}
                        onChange={(e) => handleSettingChange('general', 'maxFileUploadSize', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Session Timeout (minutes)</Form.Label>
                      <Form.Control
                        type="number"
                        min="5"
                        max="480"
                        value={systemSettings.general.sessionTimeout}
                        onChange={(e) => handleSettingChange('general', 'sessionTimeout', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Enable User Registration"
                    checked={systemSettings.general.registrationEnabled}
                    onChange={(e) => handleSettingChange('general', 'registrationEnabled', e.target.checked)}
                  />
                </Form.Group>

                <Button 
                  variant="primary" 
                  onClick={() => handleSaveSettings('general')}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save General Settings'}
                </Button>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="notifications" title="Notifications">
            <Card>
              <Card.Header>
                <h5>Notification Settings</h5>
              </Card.Header>
              <Card.Body>
                <Row className="mb-4">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Check
                        type="checkbox"
                        label="Email Notifications"
                        checked={systemSettings.notifications.emailEnabled}
                        onChange={(e) => handleSettingChange('notifications', 'emailEnabled', e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Check
                        type="checkbox"
                        label="Push Notifications"
                        checked={systemSettings.notifications.pushEnabled}
                        onChange={(e) => handleSettingChange('notifications', 'pushEnabled', e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Check
                        type="checkbox"
                        label="Weekly Reports"
                        checked={systemSettings.notifications.weeklyReportsEnabled}
                        onChange={(e) => handleSettingChange('notifications', 'weeklyReportsEnabled', e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6>Alert Thresholds</h6>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Low Attendance Threshold (%)</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        max="100"
                        value={systemSettings.notifications.alertThresholds.lowAttendance}
                        onChange={(e) => handleNestedSettingChange('notifications', 'alertThresholds', 'lowAttendance', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Document Overdue (days)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="365"
                        value={systemSettings.notifications.alertThresholds.documentOverdue}
                        onChange={(e) => handleNestedSettingChange('notifications', 'alertThresholds', 'documentOverdue', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>System Error Threshold</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="100"
                        value={systemSettings.notifications.alertThresholds.systemErrors}
                        onChange={(e) => handleNestedSettingChange('notifications', 'alertThresholds', 'systemErrors', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Button 
                  variant="primary" 
                  onClick={() => handleSaveSettings('notifications')}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Notification Settings'}
                </Button>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="documents" title="Documents">
            <Card>
              <Card.Header>
                <h5>📄 Document Submission Settings</h5>
              </Card.Header>
              <Card.Body>
                <Alert variant="info" className="mb-4">
                  Configure document submission deadlines and requirements for learners.
                </Alert>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Initial Document Upload Deadline (days from registration)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="365"
                        value={systemSettings.documents?.initialUploadDeadline || 14}
                        onChange={(e) => handleSettingChange('documents', 'initialUploadDeadline', parseInt(e.target.value))}
                      />
                      <Form.Text className="text-muted">
                        Number of days learners have to submit required documents after registration.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Extension Request Days (max days for extension)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="90"
                        value={systemSettings.documents?.maxExtensionDays || 30}
                        onChange={(e) => handleSettingChange('documents', 'maxExtensionDays', parseInt(e.target.value))}
                      />
                      <Form.Text className="text-muted">
                        Maximum number of days that can be granted as an extension.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mt-4 mb-3">Reminder Schedule</h6>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>First Reminder (days before deadline)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="14"
                        value={systemSettings.documents?.reminderDays?.first || 7}
                        onChange={(e) => handleNestedSettingChange('documents', 'reminderDays', 'first', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Second Reminder (days before deadline)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="7"
                        value={systemSettings.documents?.reminderDays?.second || 3}
                        onChange={(e) => handleNestedSettingChange('documents', 'reminderDays', 'second', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Final Reminder (days before deadline)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="3"
                        value={systemSettings.documents?.reminderDays?.final || 1}
                        onChange={(e) => handleNestedSettingChange('documents', 'reminderDays', 'final', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mt-4 mb-3">Escalation Schedule (days after deadline)</h6>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>First Escalation (days overdue)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="30"
                        value={systemSettings.documents?.escalationDays?.first || 1}
                        onChange={(e) => handleNestedSettingChange('documents', 'escalationDays', 'first', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Second Escalation (days overdue)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="60"
                        value={systemSettings.documents?.escalationDays?.second || 7}
                        onChange={(e) => handleNestedSettingChange('documents', 'escalationDays', 'second', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Final Escalation (days overdue)</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="90"
                        value={systemSettings.documents?.escalationDays?.final || 14}
                        onChange={(e) => handleNestedSettingChange('documents', 'escalationDays', 'final', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Enable automatic document reminders"
                    checked={systemSettings.documents?.autoRemindersEnabled !== false}
                    onChange={(e) => handleSettingChange('documents', 'autoRemindersEnabled', e.target.checked)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Allow facilitators to extend learner deadlines"
                    checked={systemSettings.documents?.facilitatorCanExtend !== false}
                    onChange={(e) => handleSettingChange('documents', 'facilitatorCanExtend', e.target.checked)}
                  />
                </Form.Group>

                <Button
                  variant="primary"
                  onClick={() => handleSaveSettings('documents')}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Document Settings'}
                </Button>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="security" title="Security">
            <Card>
              <Card.Header>
                <h5>🔒 Security Settings</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Minimum Password Length</Form.Label>
                      <Form.Control
                        type="number"
                        min="6"
                        max="50"
                        value={systemSettings.security.passwordMinLength}
                        onChange={(e) => handleSettingChange('security', 'passwordMinLength', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Max Login Attempts</Form.Label>
                      <Form.Control
                        type="number"
                        min="1"
                        max="20"
                        value={systemSettings.security.maxLoginAttempts}
                        onChange={(e) => handleSettingChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Check
                        type="checkbox"
                        label="Require Special Characters in Password"
                        checked={systemSettings.security.passwordRequireSpecial}
                        onChange={(e) => handleSettingChange('security', 'passwordRequireSpecial', e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Check
                        type="checkbox"
                        label="Require Two-Factor Authentication"
                        checked={systemSettings.security.twoFactorRequired}
                        onChange={(e) => handleSettingChange('security', 'twoFactorRequired', e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Button 
                  variant="primary" 
                  onClick={() => handleSaveSettings('security')}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Security Settings'}
                </Button>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="compliance" title="Compliance">
            <Card>
              <Card.Header>
                <h5>📋 Compliance & Data Retention</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Document Retention (days)</Form.Label>
                      <Form.Control
                        type="number"
                        min="30"
                        max="7300"
                        value={systemSettings.compliance.documentRetentionDays}
                        onChange={(e) => handleSettingChange('compliance', 'documentRetentionDays', parseInt(e.target.value))}
                      />
                      <Form.Text className="text-muted">
                        Legal requirement: 7 years (2555 days)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Backup Frequency</Form.Label>
                      <Form.Select
                        value={systemSettings.compliance.backupFrequency}
                        onChange={(e) => handleSettingChange('compliance', 'backupFrequency', e.target.value)}
                      >
                        <option value="hourly">Hourly</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Check
                        type="checkbox"
                        label="Enable Audit Logging"
                        checked={systemSettings.compliance.auditLogEnabled}
                        onChange={(e) => handleSettingChange('compliance', 'auditLogEnabled', e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Check
                        type="checkbox"
                        label="Enable Data Encryption"
                        checked={systemSettings.compliance.encryptionEnabled}
                        onChange={(e) => handleSettingChange('compliance', 'encryptionEnabled', e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Alert variant="info">
                  <strong>POPI Act Compliance:</strong> All settings are configured to comply with South African Protection of Personal Information Act requirements.
                </Alert>

                <Button 
                  variant="primary" 
                  onClick={() => handleSaveSettings('compliance')}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Compliance Settings'}
                </Button>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="integrations" title="Integrations">
            <GoogleFormsIntegration />
          </Tab>

          <Tab eventKey="logs" title="System Logs">
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h5>📊 Recent System Activity</h5>
                  <Button variant="outline-primary" size="sm">
                    Download Full Logs
                  </Button>
                </div>
              </Card.Header>
              <Card.Body>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Level</th>
                      <th>Message</th>
                      <th>User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {systemLogs.map((log) => (
                      <tr key={log.id}>
                        <td>
                          <small>{log.timestamp.toLocaleString()}</small>
                        </td>
                        <td>{getLevelBadge(log.level)}</td>
                        <td>{log.message}</td>
                        <td>
                          <small className="text-muted">{log.user}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Container>
    </>
  );
};

export default AdminSettings;